import { useState, useRef, useEffect, useCallback } from "react";
import html2canvas from "html2canvas";
import { Download, Upload, RefreshCw, ArrowLeft, Move, ZoomIn, Save, Check, Share2, Globe, CheckCircle2, Circle, CloudUpload } from "lucide-react";
import axios from "axios";
import { FeedCardPreview } from "./FeedCardPreview";
import { QueueNav } from "./QueueNav";
import { ShareSheet } from "./ShareSheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LDC_TEXT_LOGO = "https://customer-assets.emergentagent.com/job_artist-portal-ldc/artifacts/39dze3hq_Text%20Logo%20Gold_noBG_WEBP.webp";

// Real export resolutions
const FORMATS = {
  square: { label: "SQUARE", w: 1080, h: 1080 },
  story: { label: "STORY", w: 1080, h: 1920 },
  landscape: { label: "LANDSCAPE", w: 1920, h: 1080 },
};
const PREVIEW_MAX_W = 680;
const PREVIEW_MAX_H = 620;

const ACCENTS = {
  gold: { label: "GOLD", accent: "#C9B97A", line: "rgba(201,185,122,0.55)" },
  green: { label: "GREEN", accent: "#7ACBA4", line: "rgba(122,203,164,0.55)" },
  mono: { label: "MONO", accent: "#EAEAEA", line: "rgba(234,234,234,0.5)" },
};

const TAG_OPTIONS = [
  "NEW SIGNING",
  "NEW ENDORSER",
  "PROUDLY ENDORSING",
  "SPONSORED ARTIST",
  "FEATURED ARTIST",
  "MEET THE ARTIST",
  "ARTIST SPOTLIGHT",
];

function profilePhotoUrl(profile) {
  const p = profile?.photos?.[0];
  return p?.thumbnails?.large?.url || p?.thumbnails?.full?.url || p?.url || null;
}

async function toDataUrl(url) {
  const proxied = `${API}/image-proxy?url=${encodeURIComponent(url)}`;
  const resp = await fetch(proxied);
  if (!resp.ok) throw new Error("proxy failed");
  const blob = await resp.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function AnnouncementTemplate({ onBack, profiles = [], servicesOptions = [], token, adminTools = null }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [mode, setMode] = useState("announcement"); // announcement | sessions
  const [format, setFormat] = useState("square");
  const [accent, setAccent] = useState("gold");
  const [bgImage, setBgImage] = useState(null);
  const [natural, setNatural] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [artistName, setArtistName] = useState("ARTIST NAME");
  const [bandName, setBandName] = useState("Band / Act Name");
  const [sessionServices, setSessionServices] = useState([]);
  const [baseRegion, setBaseRegion] = useState("");
  const [caption, setCaption] = useState("");
  const [tag, setTag] = useState("NEW SIGNING");
  const [downloading, setDownloading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [feedPreview, setFeedPreview] = useState(null);

  const templateRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragState = useRef(null);

  const fmt = FORMATS[format];
  const previewScale = Math.min(PREVIEW_MAX_W / fmt.w, PREVIEW_MAX_H / fmt.h);
  const isSessions = mode === "sessions";
  const displayTag = isSessions ? "AVAILABLE FOR SESSIONS" : tag;

  const selectMode = (m) => {
    setMode(m);
    if (m === "sessions" && !caption.trim()) {
      setCaption("Book me via londondrumsticks.co.uk");
    }
    if (m === "announcement" && caption.trim() === "Book me via londondrumsticks.co.uk") {
      setCaption("");
    }
  };

  const toggleService = (opt) => {
    setSessionServices((cur) =>
      cur.includes(opt) ? cur.filter((s) => s !== opt) : [...cur, opt]
    );
  };

  const profilePhotos = (profiles[selectedIdx]?.photos || []).filter((p) => p?.url);
  const [activePhotoId, setActivePhotoId] = useState(null);

  const pickProfilePhoto = (p) => {
    const url = p?.thumbnails?.large?.url || p?.thumbnails?.full?.url || p?.url;
    if (!url) return;
    setActivePhotoId(p.id);
    setLoadingPhoto(true);
    toDataUrl(url)
      .then((dataUrl) => setImage(dataUrl))
      .catch(() => setImage(null))
      .finally(() => setLoadingPhoto(false));
  };

  useEffect(() => {
    const profile = profiles[selectedIdx];
    if (!profile) return;
    setArtistName((profile.full_name || "ARTIST NAME").toUpperCase());
    setBandName(profile.band_name || "Band / Act Name");
    setSessionServices(profile.session_services_offered || []);
    setBaseRegion(profile.base_country_region || "");
    const photo = profilePhotoUrl(profile);
    setActivePhotoId(profile.photos?.[0]?.id || null);
    if (photo) {
      setLoadingPhoto(true);
      toDataUrl(photo)
        .then((dataUrl) => setImage(dataUrl))
        .catch(() => setImage(null))
        .finally(() => setLoadingPhoto(false));
    } else {
      setImage(null);
    }
  }, [selectedIdx, profiles]);

  // Re-centre when format changes (framing region differs)
  useEffect(() => { setOffset({ x: 0, y: 0 }); }, [format]);

  const setImage = (dataUrl) => {
    setBgImage(dataUrl);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    if (dataUrl) {
      const img = new Image();
      img.onload = () => setNatural({ w: img.naturalWidth, h: img.naturalHeight });
      img.src = dataUrl;
    } else {
      setNatural(null);
    }
  };

  const loadImage = (file) => {
    setActivePhotoId(null);
    const reader = new FileReader();
    reader.onload = (e) => setImage(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) loadImage(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) loadImage(file);
  };

  // Photo size (cover * zoom) and clamped position, in REAL px
  const layout = (() => {
    if (!natural) return null;
    const coverScale = Math.max(fmt.w / natural.w, fmt.h / natural.h);
    const w = natural.w * coverScale * zoom;
    const h = natural.h * coverScale * zoom;
    const clamp = (v, min) => Math.min(0, Math.max(min, v));
    const left = clamp((fmt.w - w) / 2 + offset.x, fmt.w - w);
    const top = clamp((fmt.h - h) / 2 + offset.y, fmt.h - h);
    return { w, h, left, top };
  })();

  const onPointerDown = (e) => {
    if (!bgImage) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const dx = (e.clientX - dragState.current.startX) / previewScale;
    const dy = (e.clientY - dragState.current.startY) / previewScale;
    setOffset({ x: dragState.current.ox + dx, y: dragState.current.oy + dy });
  };
  const onPointerUp = () => { dragState.current = null; };

  const reCenter = useCallback(() => setOffset({ x: 0, y: 0 }), []);

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const captureCanvas = async (f) =>
    html2canvas(templateRef.current, {
      scale: 1,
      width: f.w,
      height: f.h,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#0C120E",
      logging: false,
    });

  const triggerDownload = (dataUrl, key) => {
    const link = document.createElement("a");
    link.download = `LDC-${artistName.replace(/\s+/g, "-")}-${key}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleDownload = async () => {
    if (!templateRef.current) return;
    setDownloading(true);
    setCapturing(true);
    await wait(120);
    try {
      const canvas = await captureCanvas(fmt);
      triggerDownload(canvas.toDataURL("image/png"), format);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setCapturing(false);
      setDownloading(false);
    }
  };

  const handleSaveToProfile = async () => {
    const profile = profiles[selectedIdx];
    if (!templateRef.current || !profile?.record_id || !token) return;
    setSaving(true);
    setCapturing(true);
    setSavedOk(false);
    await wait(120);
    try {
      const canvas = await captureCanvas(fmt);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const form = new FormData();
      form.append("record_id", profile.record_id);
      form.append("poster_type", isSessions ? "sessions" : "announcement");
      form.append("file", blob, `LDC-${artistName.replace(/\s+/g, "-")}-${mode}.png`);
      await axios.post(`${API}/artist/save-announcement`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 4000);
    } catch (err) {
      console.error("Save to profile failed:", err);
      alert("Could not save to your profile. Please try again.");
    } finally {
      setCapturing(false);
      setSaving(false);
    }
  };

  const fileName = `LDC-${artistName.replace(/\s+/g, "-")}-${format}.png`;
  const shareText = `${artistName} — ${displayTag}${caption.trim() ? ` · ${caption.trim()}` : ""} londondrumsticks.co.uk`;

  const capturePoster = async () => {
    setCapturing(true);
    await wait(120);
    try {
      return await captureCanvas(fmt);
    } finally {
      setCapturing(false);
    }
  };

  const handleShare = async () => {
    if (!templateRef.current) return;
    setSharing(true);
    try {
      const canvas = await capturePoster();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: artistName, text: shareText });
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }
      setShareData(canvas.toDataURL("image/png"));
    } catch (err) {
      console.error("Share failed:", err);
    } finally {
      setSharing(false);
    }
  };

  const handleFeedPreview = async () => {
    if (!templateRef.current) return;
    try {
      const canvas = await capturePoster();
      setFeedPreview(canvas.toDataURL("image/png"));
    } catch (err) {
      console.error("Preview failed:", err);
    }
  };

  const [pushing, setPushing] = useState(false);
  const [pushedCount, setPushedCount] = useState(0);
  const [pushedEmail, setPushedEmail] = useState(null);

  const handlePushAll = async () => {
    const profile = profiles[selectedIdx];
    if (!templateRef.current || !profile?.record_id || !token) return;
    const original = format;
    setPushing(true);
    setCapturing(true);
    setPushedCount(0);
    try {
      const form = new FormData();
      form.append("record_id", profile.record_id);
      for (const key of ["square", "story", "landscape"]) {
        setFormat(key);
        await wait(260);
        const canvas = await captureCanvas(FORMATS[key]);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
        form.append("files", blob, `LDC-${artistName.replace(/\s+/g, "-")}-${key}.png`);
      }
      const { data } = await axios.post(`${API}/artist/save-posters`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPushedCount(data.added);
      setPushedEmail(data.emailed ? data.emailed_to : null);
      adminTools?.onPushed?.(data.profile);
      setTimeout(() => { setPushedCount(0); setPushedEmail(null); }, 8000);
    } catch (err) {
      console.error("Push to Airtable failed:", err);
      alert("Could not push posters to Airtable. Please try again.");
    } finally {
      setFormat(original);
      setCapturing(false);
      setPushing(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!templateRef.current) return;
    const original = format;
    setDownloadingAll(true);
    setCapturing(true);
    try {
      for (const key of ["square", "story", "landscape"]) {
        setFormat(key);
        await wait(260); // allow re-render + photo re-centre
        const canvas = await captureCanvas(FORMATS[key]);
        triggerDownload(canvas.toDataURL("image/png"), key);
        await wait(350); // let the browser process each download
      }
    } catch (err) {
      console.error("Batch download failed:", err);
    } finally {
      setFormat(original);
      setCapturing(false);
      setDownloadingAll(false);
    }
  };

  const busy = downloading || downloadingAll || saving || sharing || pushing;
  const queue = adminTools?.queue;
  useEffect(() => {
    if (!queue) return;
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey || shareData || feedPreview) return;
      if (e.key === "ArrowRight" && queue.onNext) { e.preventDefault(); if (!busy) queue.onNext(); }
      else if (e.key === "ArrowLeft" && queue.onPrev) { e.preventDefault(); if (!busy) queue.onPrev(); }
      else if (e.key === "d" || e.key === "D") { e.preventDefault(); if (!busy) handleDownload(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="announce-page">
      <div className="announce-topbar">
        <div className="admin-header-left">
          <button className="btn-back" onClick={onBack}>
            <ArrowLeft size={16} /> {adminTools ? "BACK TO ARTIST" : "BACK TO PORTAL"}
          </button>
          {queue && <QueueNav nav={queue} />}
          {queue && (
            <span className="kbd-hints" data-testid="kbd-hints">
              <kbd>←</kbd><kbd>→</kbd> artist <kbd>D</kbd> download
            </span>
          )}
        </div>
        <h1 className="announce-topbar-title">ANNOUNCEMENT TEMPLATE</h1>
        <div className="announce-topbar-actions">
          {adminTools && !isSessions && (
            <button
              className={`btn-back btn-announced ${adminTools.announced ? "is-on" : ""}`}
              onClick={adminTools.onToggle}
              disabled={adminTools.busy}
              data-testid="mark-announced-btn"
            >
              {adminTools.busy ? <RefreshCw size={16} className="spin" /> : adminTools.announced ? <CheckCircle2 size={16} /> : <Circle size={16} />}
              {adminTools.announced ? "ANNOUNCED" : "MARK AS ANNOUNCED"}
            </button>
          )}
          {isSessions && (
            <button
              className="btn-back"
              onClick={handleFeedPreview}
              disabled={saving || downloading || downloadingAll || sharing}
              data-testid="feed-preview-btn"
            >
              <Globe size={16} /> PREVIEW ON SITE
            </button>
          )}
          <button
            className="btn-back"
            onClick={handleShare}
            disabled={busy}
            data-testid="share-btn"
          >
            {sharing ? <RefreshCw size={16} className="spin" /> : <Share2 size={16} />}
            {sharing ? "PREPARING..." : "SHARE"}
          </button>
          {adminTools && !isSessions ? (
            <button
              className={`btn-back btn-push-airtable ${pushedCount ? "is-done" : ""}`}
              onClick={handlePushAll}
              disabled={busy}
              data-testid="push-all-btn"
              title="Adds Square, Story and Landscape to this artist's Announcement Poster column in Airtable and emails them to the artist"
            >
              {pushing ? <RefreshCw size={16} className="spin" /> : pushedCount ? <Check size={16} /> : <CloudUpload size={16} />}
              {pushing ? "PUSHING TO AIRTABLE..." : pushedCount ? (pushedEmail ? `${pushedCount} PUSHED · EMAILED TO ARTIST` : `${pushedCount} PUSHED TO AIRTABLE`) : "PUSH ALL 3 + EMAIL ARTIST"}
            </button>
          ) : (
            <button
              className="btn-back btn-save-profile"
              onClick={handleSaveToProfile}
              disabled={busy}
              data-testid="save-profile-btn"
            >
              {saving ? <RefreshCw size={16} className="spin" /> : savedOk ? <Check size={16} /> : <Save size={16} />}
              {saving ? "SAVING..." : savedOk ? "SAVED" : adminTools ? "SAVE TO SESSION POSTER" : "SAVE TO PROFILE"}
            </button>
          )}
          <button
            className="btn-back btn-download-all"
            onClick={handleDownloadAll}
            disabled={busy}
            data-testid="download-all-btn"
          >
            {downloadingAll ? <RefreshCw size={16} className="spin" /> : <Download size={16} />}
            {downloadingAll ? "GENERATING..." : "DOWNLOAD ALL 3"}
          </button>
          <button
            className="btn-download"
            onClick={handleDownload}
            disabled={busy}
            data-testid="download-btn"
          >
            {downloading ? <RefreshCw size={16} className="spin" /> : <Download size={16} />}
            {downloading ? "GENERATING..." : "DOWNLOAD PNG"}
          </button>
        </div>
      </div>

      <div className="announce-body">
        {/* Controls */}
        <aside className="announce-controls">
          <h2 className="controls-title">CUSTOMISE</h2>

          {/* Mode */}
          <div className="control-group">
            <label className="control-label">STYLE</label>
            <div className="format-options">
              <button
                className={`tag-option ${!isSessions ? "active" : ""}`}
                onClick={() => selectMode("announcement")}
                data-testid="mode-announcement"
              >
                ANNOUNCEMENT
              </button>
              <button
                className={`tag-option ${isSessions ? "active" : ""}`}
                onClick={() => selectMode("sessions")}
                data-testid="mode-sessions"
              >
                AVAILABLE FOR SESSIONS
              </button>
            </div>
          </div>

          {profiles.length > 1 && (
            <div className="control-group">
              <label className="control-label">PROFILE</label>
              <select
                className="control-input"
                value={selectedIdx}
                onChange={(e) => setSelectedIdx(Number(e.target.value))}
                data-testid="profile-select"
              >
                {profiles.map((p, i) => (
                  <option key={p.record_id} value={i}>
                    {p.full_name || `Profile ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Format */}
          <div className="control-group">
            <label className="control-label">FORMAT</label>
            <div className="format-options">
              {Object.entries(FORMATS).map(([key, f]) => (
                <button
                  key={key}
                  className={`tag-option ${format === key ? "active" : ""}`}
                  onClick={() => setFormat(key)}
                  data-testid={`format-${key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Accent colour */}
          <div className="control-group">
            <label className="control-label">ACCENT COLOUR</label>
            <div className="format-options">
              {Object.entries(ACCENTS).map(([key, a]) => (
                <button
                  key={key}
                  className={`tag-option accent-option ${accent === key ? "active" : ""}`}
                  onClick={() => setAccent(key)}
                  data-testid={`accent-${key}`}
                >
                  <span className="accent-dot" style={{ background: a.accent }} />
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Image upload */}
          <div className="control-group">
            <label className="control-label">BACKGROUND IMAGE</label>
            {profilePhotos.length > 1 && (
              <div className="photo-picker" data-testid="photo-picker">
                {profilePhotos.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    className={`photo-pick ${activePhotoId === p.id ? "active" : ""}`}
                    onClick={() => pickProfilePhoto(p)}
                    data-testid={`photo-pick-${p.id}`}
                  >
                    <img src={p.thumbnails?.small?.url || p.thumbnails?.large?.url || p.url} alt="" />
                  </button>
                ))}
              </div>
            )}
            <div
              className={`drop-zone ${dragOver ? "drag-over" : ""} ${bgImage ? "has-image" : ""}`}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              data-testid="drop-zone"
            >
              {bgImage ? (
                <>
                  <img src={bgImage} alt="preview" className="drop-preview" />
                  <div className="drop-replace"><Upload size={14} /> REPLACE</div>
                </>
              ) : loadingPhoto ? (
                <>
                  <RefreshCw size={24} className="drop-icon spin" />
                  <p>LOADING PHOTO…</p>
                </>
              ) : (
                <>
                  <Upload size={24} className="drop-icon" />
                  <p>DROP IMAGE HERE</p>
                  <p className="drop-sub">or click to browse</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>

          {/* Position & zoom */}
          {bgImage && (
            <div className="control-group">
              <label className="control-label">
                <ZoomIn size={13} /> ZOOM & POSITION
              </label>
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="zoom-slider"
                data-testid="zoom-slider"
              />
              <p className="adjust-hint">
                <Move size={12} /> Drag the photo in the preview to reposition.
                <button type="button" className="recenter-btn" onClick={reCenter} data-testid="recenter-btn">
                  Re-centre
                </button>
              </p>
            </div>
          )}

          {/* Tag selector (announcement mode) */}
          {!isSessions && (
            <div className="control-group">
              <label className="control-label">ANNOUNCEMENT TAG</label>
              <div className="tag-options">
                {TAG_OPTIONS.map((t) => (
                  <button
                    key={t}
                    className={`tag-option ${tag === t ? "active" : ""}`}
                    onClick={() => setTag(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Session details (sessions mode) */}
          {isSessions && (
            <>
              <div className="control-group">
                <label className="control-label">SERVICES OFFERED</label>
                <div className="chip-picker" data-testid="svc-picker">
                  {servicesOptions.length === 0 && (
                    <span className="field-empty">No services configured in Airtable.</span>
                  )}
                  {servicesOptions.map((opt) => {
                    const selected = sessionServices.includes(opt);
                    return (
                      <button
                        type="button"
                        key={opt}
                        className={`chip ${selected ? "chip-selected" : ""}`}
                        onClick={() => toggleService(opt)}
                        data-testid={`svc-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="control-group">
                <label className="control-label">BASED IN</label>
                <input
                  className="control-input"
                  value={baseRegion}
                  onChange={(e) => setBaseRegion(e.target.value)}
                  placeholder="e.g. London, UK"
                  data-testid="base-region-input"
                />
              </div>
            </>
          )}

          {/* Artist name */}
          <div className="control-group">
            <label className="control-label">ARTIST NAME</label>
            <input
              className="control-input"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value.toUpperCase())}
              placeholder="ARTIST NAME"
              data-testid="artist-name-input"
            />
          </div>

          {/* Band name */}
          <div className="control-group">
            <label className="control-label">BAND / ACT</label>
            <input
              className="control-input"
              value={bandName}
              onChange={(e) => setBandName(e.target.value)}
              placeholder="Band Name"
              data-testid="band-name-input"
            />
          </div>

          {/* Caption */}
          <div className="control-group">
            <label className="control-label">CAPTION (OPTIONAL)</label>
            <input
              className="control-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Available for sessions worldwide"
              maxLength={60}
              data-testid="caption-input"
            />
          </div>
        </aside>

        {/* Template preview */}
        <div className="announce-preview-wrap">
          <p className="preview-label">{fmt.w} × {fmt.h} PREVIEW</p>

          <div
            className="template-viewport"
            style={{ width: `${fmt.w * previewScale}px`, height: `${fmt.h * previewScale}px` }}
          >
            <div className="template-scaler" style={{ transform: capturing ? "scale(1)" : `scale(${previewScale})` }}>
              <div
                className={`announcement-template${isSessions ? " is-sessions" : ""}`}
                ref={templateRef}
                style={{ width: `${fmt.w}px`, height: `${fmt.h}px`, backgroundColor: "#0C120E", "--accent": ACCENTS[accent].accent, "--accent-line": ACCENTS[accent].line }}
              >
                {/* Photo layer (draggable) */}
                <div
                  className={`tmpl-photo-layer ${bgImage ? "draggable" : ""}`}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerLeave={onPointerUp}
                  data-testid="photo-layer"
                >
                  {bgImage && layout && (
                    <img
                      src={bgImage}
                      alt=""
                      className="tmpl-photo"
                      draggable={false}
                      style={{
                        width: `${layout.w}px`,
                        height: `${layout.h}px`,
                        left: `${layout.left}px`,
                        top: `${layout.top}px`,
                      }}
                    />
                  )}
                </div>

                {/* Bottom-up fade */}
                <div className="tmpl-overlay" />

                {/* Gold frame border */}
                <div className="tmpl-frame" />
                <div className="tmpl-frame-corner tmpl-corner-tl" />
                <div className="tmpl-frame-corner tmpl-corner-tr" />
                <div className="tmpl-frame-corner tmpl-corner-bl" />
                <div className="tmpl-frame-corner tmpl-corner-br" />

                {/* Middle block: tag + artist */}
                <div className="tmpl-mid">
                  <div className="tmpl-tag-wrap">
                    <div className="tmpl-rule" />
                    <span className="tmpl-tag">{displayTag}</span>
                    <div className="tmpl-rule" />
                  </div>
                  <div className="tmpl-artist">
                    <h1 className="tmpl-name">{artistName}</h1>
                    <p className="tmpl-band">{bandName}</p>
                  </div>
                  {isSessions && (
                    <div className="tmpl-sessions">
                      {sessionServices.length > 0 && (
                        <div className={`tmpl-svc-chips${sessionServices.length >= 4 ? " compact" : ""}`}>
                          {sessionServices.map((s) => (
                            <span key={s} className="tmpl-svc-chip">{s}</span>
                          ))}
                        </div>
                      )}
                      {baseRegion.trim() && (
                        <p className="tmpl-region">BASED IN {baseRegion.toUpperCase()}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Bottom: caption + text logo + URL */}
                <div className="tmpl-bottom">
                  {caption.trim() && (
                    <p className="tmpl-caption" data-testid="tmpl-caption">{caption}</p>
                  )}
                  <div className="tmpl-bottom-rule" />
                  <img src={LDC_TEXT_LOGO} alt="London Drumstick Co." className="tmpl-text-logo" crossOrigin="anonymous" />
                  <p className="tmpl-url">LONDONDRUMSTICKS.CO.UK</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {feedPreview && (
        <FeedCardPreview
          posterUrl={feedPreview}
          profile={profiles[selectedIdx]}
          name={artistName}
          band={bandName}
          services={sessionServices}
          basedIn={baseRegion}
          onClose={() => setFeedPreview(null)}
        />
      )}
      {shareData && (
        <ShareSheet
          dataUrl={shareData}
          fileName={fileName}
          caption={shareText}
          onClose={() => setShareData(null)}
        />
      )}
    </div>
  );
}
